'use client';

/* eslint-disable react-perf/jsx-no-new-function-as-prop */
import React, {useCallback, useEffect, useState} from 'react';
import useEmblaCarousel from 'embla-carousel-react';

import type {KeyedArray} from '../../shared/types';
import {Figure} from '../figure';
import {ImageBlock} from '../image/ImageBlock';
import type {ImageWeb} from '../image/types';

import {Thumb} from './thumb';

import './embla.css';

type PropType = {
	images: KeyedArray<ImageWeb>;
};

const Gallery: React.FC<PropType> = (props) => {
	const {images} = props;
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [emblaMainRef, emblaMainApi] = useEmblaCarousel();
	const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
		containScroll: 'keepSnaps',
		dragFree: true,
	});

	const onThumbClick = useCallback(
		(index: number) => {
			if (!emblaMainApi || !emblaThumbsApi) return;
			emblaMainApi.scrollTo(index);
		},
		[emblaMainApi, emblaThumbsApi],
	);

	const onSelect = useCallback(() => {
		if (!emblaMainApi || !emblaThumbsApi) return;
		setSelectedIndex(emblaMainApi.selectedScrollSnap());
		emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
	}, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

	useEffect(() => {
		if (!emblaMainApi) return;
		onSelect();

		emblaMainApi.on('select', onSelect).on('reInit', onSelect);
	}, [emblaMainApi, onSelect]);

	return (
		<div className="embla">
			<div className="embla__viewport" ref={emblaMainRef}>
				<div className="embla__container">
					{images.map((image) => (
						<div className="embla__slide" key={image._key}>
							<div className="embla__slide__number">
								{image.caption || image.credit ? (
									<Figure caption={image.caption}>
										<ImageBlock
											src={image.image.src}
											alt={image.altText ?? ''}
											height={image.image.height}
											width={image.image.width}
										/>
									</Figure>
								) : (
									<ImageBlock
										src={image.image.src}
										alt={image.altText ?? ''}
										height={image.image.height}
										width={image.image.width}
									/>
								)}
							</div>
						</div>
					))}
				</div>
			</div>

			<div className="embla-thumbs">
				<div className="embla-thumbs__viewport" ref={emblaThumbsRef}>
					<div className="embla-thumbs__container">
						{images.map((image, index) => (
							<Thumb
								key={image._key}
								image={image}
								onClick={() => onThumbClick(index)}
								selected={index === selectedIndex}
								index={index}
								total={images.length}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Gallery;
